import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonIcon,
  IonList,
  IonSpinner,
  IonText,
} from "@ionic/react";
import {
  notificationsOutline,
  checkmarkDoneOutline,
  peopleOutline,
  briefcaseOutline,
  checkmarkCircleOutline,
  removeCircleOutline,
  alertCircleOutline,
} from "ionicons/icons";
import {
  collection,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { Redirect, useHistory } from "react-router-dom";
import { db } from "../../firebase";
import { ProfileContext } from "../../context/ProfileContext";
import { AuthContext } from "../../context/AuthContext";
import { LoadingScreen } from "../../components/LoadingScreen";
import { WebLayout } from "../../components/layouts/WebLayout";
import { PUSH_NOTIFICATION_TYPES } from "../../functions/postActionTypes";
import { useNotificationTapHandlers } from "../../hooks/useNotificationTapHandlers";

interface AppNotification {
  id: string;
  type?: string;
  title?: string;
  body?: string;
  createdAt?: any;
  isRead?: boolean;
  readAt?: any;
  fromUID?: string;
  toUID?: string;
  additionalDetails?: any;
}

const formatNotificationTime = (createdAt: any) => {
  try {
    if (!createdAt) return "";

    const date = typeof createdAt?.toDate === "function" ? createdAt.toDate() : new Date(createdAt);

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;

    return date.toLocaleDateString();
  } catch (error) {
    return "";
  }
};

const getNotificationIcon = (type?: string) => {
  switch (type) {
    case PUSH_NOTIFICATION_TYPES.AssociationRequest:
    case PUSH_NOTIFICATION_TYPES.SchedulerNewAssociateFromInvite:
      return peopleOutline;

    case PUSH_NOTIFICATION_TYPES.WerkerJobAvailable:
    case PUSH_NOTIFICATION_TYPES.WerkerJobReminder:
      return briefcaseOutline;

    case PUSH_NOTIFICATION_TYPES.SchedulerAddedWerkerToShift:
      return checkmarkCircleOutline;

    case PUSH_NOTIFICATION_TYPES.SchedulerRemovedWerkerFromShift:
      return removeCircleOutline;

    case PUSH_NOTIFICATION_TYPES.SchedulerFilledJobs:
    case PUSH_NOTIFICATION_TYPES.SchedulerUnFilledJobs:
      return alertCircleOutline;

    default:
      return notificationsOutline;
  }
};

const NotificationsDesktop: React.FC = () => {
  const { currentUser, authReady } = useContext(AuthContext);
  const { profile } = useContext(ProfileContext) as any;

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [segment] = useState<"all" | "unread">("all");
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const uid = profile?.UserId;
  const history = useHistory();

  useEffect(() => {
    if (!uid) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const q = query(collection(db, `users/${uid}/notifications`), orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const next: AppNotification[] = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        setNotifications(next);
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  const unreadCount = useMemo(() => {
    return notifications.filter((item) => !item.isRead).length;
  }, [notifications]);

  const visibleNotifications = useMemo(() => {
    if (segment === "unread") {
      return notifications.filter((item) => !item.isRead);
    }
    return notifications;
  }, [notifications, segment]);

  const markOneRead = useCallback(async (notificationId: string, alreadyRead?: boolean) => {
    if (!uid || alreadyRead) return;

    try {
      await updateDoc(doc(db, `users/${uid}/notifications/${notificationId}`), {
        isRead: true,
        readAt: serverTimestamp(),
      });

      const notificationsCountRef = doc(db, `users/${uid}/users_NotificationsCount`, uid);

      await setDoc(
        notificationsCountRef,
        {
          notificationsCount: increment(-1),
          uid,
        },
        { merge: true }
      );
    } catch (error) {
      // no-op
    }
  }, [uid]);

  const markAllRead = async () => {
    if (!uid) return;

    const unreadItems = notifications.filter((item) => !item.isRead);
    if (!unreadItems.length) return;

    try {
      setMarkingAllRead(true);

      const batch = writeBatch(db);

      unreadItems.forEach((item) => {
        const ref = doc(db, `users/${uid}/notifications/${item.id}`);
        batch.update(ref, {
          isRead: true,
          readAt: serverTimestamp(),
        });
      });

      await batch.commit();

      const notificationsCountRef = doc(db, `users/${uid}/users_NotificationsCount`, uid);

      await setDoc(
        notificationsCountRef,
        {
          notificationsCount: 0,
          uid,
        },
        { merge: true }
      );
    } catch (error) {
      // no-op
    } finally {
      setMarkingAllRead(false);
    }
  };

  const {
    navigateToWerkerShift,
    handleWerkerJobTap,
    handleSchedulerAddedTap,
    handleSchedulerEditedJobTap,
    handleSchedulerRemovedTap,
    handleSchedulerFilledJobTap,
  } = useNotificationTapHandlers(uid ?? "");

    const handleNotificationTap = useCallback(
    async (item: AppNotification) => {
      markOneRead(item.id, item.isRead);

      const details = item.additionalDetails;

      switch (item.type) {
        case PUSH_NOTIFICATION_TYPES.AssociationRequest:
          if (item.fromUID) {
            history.push(`/AssociateProfile/${item.fromUID}`);
          }
          break;

        case PUSH_NOTIFICATION_TYPES.SchedulerNewAssociateFromInvite: {
          const werkerUid = details?.werkerUid || item.fromUID;
          if (werkerUid) history.push(`/AssociateProfile/${werkerUid}`);
          break;
        }

        case PUSH_NOTIFICATION_TYPES.SchedulerFilledJobs:
        case PUSH_NOTIFICATION_TYPES.SchedulerUnFilledJobs:
          if (details?.uniqueJobDocId) await handleSchedulerFilledJobTap(details.uniqueJobDocId);
          break;

        case PUSH_NOTIFICATION_TYPES.SchedulerEditedJob:
          if (details?.werkerJobDocId && details?.uniqueJobDocId) {
            await handleSchedulerEditedJobTap(details.werkerJobDocId, details.uniqueJobDocId);
          }
          break;
        case PUSH_NOTIFICATION_TYPES.SchedulerRemovedWerkerFromShift:
          if (details?.werkerJobDocId && details?.uniqueJobDocId) {
            await handleSchedulerRemovedTap(details.werkerJobDocId, details.uniqueJobDocId);
          }
          break;
        case PUSH_NOTIFICATION_TYPES.SchedulerAddedWerkerToShift:
          if (details?.werkerJobDocId && details?.uniqueJobDocId && details?.schedUid) {
            await handleSchedulerAddedTap(details.werkerJobDocId, details.uniqueJobDocId, details.schedUid);
          } else if (details?.uniqueJobDocId && details?.schedUid) {
            await navigateToWerkerShift(details.uniqueJobDocId, details.schedUid);
          }
          break;

        case PUSH_NOTIFICATION_TYPES.WerkerJobAvailable:
        case PUSH_NOTIFICATION_TYPES.WerkerJobReminder:
          if (details?.uniqueJobDocId && details?.schedUid) {
            await handleWerkerJobTap(details.uniqueJobDocId, details.schedUid);
          }
          break;

        default:
          break;
      }
    },
    [history, markOneRead, navigateToWerkerShift, handleWerkerJobTap, handleSchedulerFilledJobTap, handleSchedulerAddedTap, handleSchedulerRemovedTap, handleSchedulerEditedJobTap]
  );

  if (!authReady) {
    return <LoadingScreen LoadingText="Loading" />;
  }

  if (currentUser.uid === "") {
    return <Redirect to={{ pathname: "/login", search: "?returnUrl=/portal" }} />;
  }

  return (
    <WebLayout title="Notifications">
      <div style={{ height: "100%", overflowY: "auto", padding: "16px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto", padding: "16px 0" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
            <IonButton
              size="small"
              fill="outline"
              onClick={markAllRead}
              disabled={markingAllRead || unreadCount === 0}
            >
              <IonIcon icon={checkmarkDoneOutline} slot="start" />
              {markingAllRead ? "Working..." : "Mark All Read"}
            </IonButton>
          </div>

          {loading ? (
            <div style={{ padding: "24px 16px", display: "flex", justifyContent: "center" }}>
              <IonSpinner name="crescent" />
            </div>
          ) : visibleNotifications.length === 0 ? (
            <IonCard>
              <IonCardContent style={{ padding: 20, textAlign: "center" }}>
                <IonIcon
                  icon={notificationsOutline}
                  style={{ fontSize: 42, opacity: 0.65, marginBottom: 10 }}
                />
                <IonText>
                  <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>
                    {segment === "unread" ? "No unread notifications" : "No notifications yet"}
                  </h2>
                </IonText>
                <IonText color="medium">
                  <p style={{ marginTop: 8 }}>
                    {segment === "unread" ? "You're all caught up." : "Important non-chat events will appear here."}
                  </p>
                </IonText>
              </IonCardContent>
            </IonCard>
          ) : (
            <IonList lines="none" style={{ padding: "0 0 16px 0" }}>
              {visibleNotifications.map((item) => {
                const isUnread = !item.isRead;

                return (
                  <IonCard
                    key={item.id}
                    button={true}
                    onClick={() => handleNotificationTap(item)}
                    style={{
                      margin: "8px 0",
                      borderLeft: isUnread
                        ? `4px solid var(--ion-color-${profile?.UserLastRoleScheduler ? "secondwarning" : "warning"})`
                        : "4px solid transparent",
                      borderRadius: "5px",
                      opacity: isUnread ? 1 : 0.9,
                    }}
                  >
                    <IonCardContent style={{ padding: 14 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            flex: 1,
                            minWidth: 0,
                          }}
                        >
                          <IonIcon
                            icon={getNotificationIcon(item.type)}
                            color="medium"
                            style={{
                              fontSize: 22,
                              minWidth: 22,
                              marginTop: 2,
                            }}
                          />

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 4,
                              }}
                            >
                              <IonText>
                                <h3
                                  style={{
                                    margin: 0,
                                    fontSize: "1rem",
                                    fontWeight: isUnread ? 700 : 600,
                                  }}
                                >
                                  {item.title || "Notification"}
                                </h3>
                              </IonText>
                            </div>

                            <IonText color="medium">
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: "0.95rem",
                                  lineHeight: 1.35,
                                  whiteSpace: "normal",
                                }}
                              >
                                {item.body || ""}
                              </p>
                            </IonText>
                          </div>
                        </div>

                        <IonText color="medium">
                          <p
                            style={{
                              margin: 0,
                              fontSize: "0.78rem",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatNotificationTime(item.createdAt)}
                          </p>
                        </IonText>
                      </div>
                    </IonCardContent>
                  </IonCard>
                );
              })}
            </IonList>
          )}
        </div>
      </div>
    </WebLayout>
  );
};

export default NotificationsDesktop;
